import { ThemeButton } from './Theme';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import type { Order } from '../services/api';
import { Send, AlertCircle, CheckCircle, Zap, Pencil, X } from 'lucide-react';
import ContractPicker from './ManualOrderEntry/ContractPicker';
import OrderExecutionOptionsFields from './ManualOrderEntry/OrderExecutionOptionsFields';
import OrderPricingFields from './ManualOrderEntry/OrderPricingFields';
import OrderSideQuantityFields from './ManualOrderEntry/OrderSideQuantityFields';
import {
    isFutureContract,
    isQualifiedContract,
    type ContractSelection,
} from './ManualOrderEntry/contractSelection';
import { buildManualOrderPrefillState, type ManualOrderPrefill } from './ManualOrderEntry/orderPrefill';
import { useManualOrderPositionSuggestions } from '../hooks/useManualOrderPositionSuggestions';
import { useManualOrderPreparedOrder } from '../hooks/useManualOrderPreparedOrder';
import { useManualOrderContractLookup } from '../hooks/useManualOrderContractLookup';
import { useManualOrderQuotes } from '../hooks/useManualOrderQuotes';
import { useManualOrderSubmission } from '../hooks/useManualOrderSubmission';
import type { PreparedNQMMOrderState } from './ManualOrderEntry/preparedOrder';

export type { ManualOrderPrefill } from './ManualOrderEntry/orderPrefill';

interface ManualOrderEntryProps {
    editingOrder?: Order | null;
    prefillOrder?: ManualOrderPrefill | null;
    preparedNQMMOrder?: PreparedNQMMOrderState | null;
    onCancelEditing?: () => void;
    onCancel?: () => void;
    onOrderModified?: () => void;
    onOrderSubmitted?: () => void;
    showCancelButton?: boolean;
    inModal?: boolean;
}

const ManualOrderEntry: React.FC<ManualOrderEntryProps> = ({
    editingOrder = null,
    prefillOrder = null,
    preparedNQMMOrder = null,
    onCancelEditing,
    onCancel,
    onOrderModified,
    onOrderSubmitted,
    showCancelButton = false,
    inModal = false,
}) => {
    const [symbol, setSymbol] = useState('');
    const [secType, setSecType] = useState('STK');
    const [strategyName, setStrategyName] = useState<'MANUAL' | 'NQMM'>('MANUAL');
    const [action, setAction] = useState<'BUY' | 'SELL'>('BUY');
    const [orderType, setOrderType] = useState('MARKET');
    const [tif, setTif] = useState<'DAY' | 'GTC'>('DAY');
    const [outsideRth, setOutsideRth] = useState(false);
    const [price, setPrice] = useState<string>('');
    const [trailingAmount, setTrailingAmount] = useState<string>('');
    const [trailType, setTrailType] = useState<'amount' | 'percent'>('amount');
    const [quantity, setQuantity] = useState<string>('1');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [unsupportedOrderType, setUnsupportedOrderType] = useState<string | null>(null);
    const [showDropdown, setShowDropdown] = useState(false);
    const [selectedContract, setSelectedContract] = useState<ContractSelection | null>(null);
    const initialOrderTypeEffect = useRef(true);
    const {
        lookupResults,
        setLookupResults,
        lookupLoading,
        handleLookup,
    } = useManualOrderContractLookup({
        symbol,
        secType,
        setError,
        setShowDropdown,
    });
    const {
        marketPrice,
        fetchingPrice,
        marketPricePresentation,
        clearQuote,
        setQuoteSnapshot,
        fetchQuote,
        fetchContractQuote,
        refreshSelectedQuote,
    } = useManualOrderQuotes({ symbol, secType, selectedContract });
    const positionSuggestions = useManualOrderPositionSuggestions();

    const resetForm = useCallback(() => {
        setSymbol('');
        setSecType('STK');
        setStrategyName('MANUAL');
        setAction('BUY');
        setOrderType('MARKET');
        setTif('DAY');
        setOutsideRth(false);
        setQuantity('1');
        setPrice('');
        setTrailingAmount('');
        setTrailType('amount');
        setUnsupportedOrderType(null);
        clearQuote();
        setSelectedContract(null);
        setLookupResults([]);
        setShowDropdown(false);
    }, [clearQuote, setLookupResults]);

    useEffect(() => {
        const orderToPrefill = (editingOrder || prefillOrder) as ManualOrderPrefill | null;
        if (!orderToPrefill) {
            setUnsupportedOrderType(null);
            return;
        }

        const prefillState = buildManualOrderPrefillState(orderToPrefill, Boolean(editingOrder));
        setSymbol(prefillState.symbol);
        setSecType(prefillState.secType);
        setAction(prefillState.action);
        setStrategyName(prefillState.strategyName);
        setOrderType(prefillState.orderType);
        setUnsupportedOrderType(prefillState.unsupportedOrderType);
        setTif(prefillState.tif);
        setOutsideRth(prefillState.outsideRth);
        setQuantity(prefillState.quantity);
        setPrice(prefillState.price);
        setTrailType(prefillState.trailType);
        setTrailingAmount(prefillState.trailingAmount);
        clearQuote();
        setSelectedContract(prefillState.selectedContract);
        setLookupResults([]);
        setShowDropdown(false);
        setError(prefillState.error);
        setSuccess(null);
    }, [editingOrder, prefillOrder, clearQuote, setLookupResults]);

    const applyPreparedNQMMOrder = useCallback((prepared: PreparedNQMMOrderState) => {
        setSymbol(prepared.symbol);
        setSecType(prepared.secType);
        setStrategyName('NQMM');
        setAction(prepared.action);
        setOrderType(prepared.orderType);
        setTif('DAY');
        setOutsideRth(false);
        setQuantity(prepared.quantity);
        setPrice(prepared.price);
        setTrailingAmount('');
        setUnsupportedOrderType(null);
        setSelectedContract(prepared.selectedContract);
        setLookupResults([]);
        setShowDropdown(false);
        setError(null);
        setSuccess(null);
    }, [setLookupResults]);
    useEffect(() => {
        if (preparedNQMMOrder) {
            applyPreparedNQMMOrder(preparedNQMMOrder);
        }
    }, [applyPreparedNQMMOrder, preparedNQMMOrder]);
    useManualOrderPreparedOrder({
        onCancelEditing,
        onPreparedOrder: applyPreparedNQMMOrder,
    });

    useEffect(() => {
        if (initialOrderTypeEffect.current) {
            initialOrderTypeEffect.current = false;
            return;
        }
        if (orderType === 'MARKET') {
            setPrice('');
            setTrailingAmount('');
        } else if (orderType !== 'TRAIL') {
            setTrailingAmount('');
        } else {
            setPrice('');
        }
    }, [orderType]);

    // Use the latest usable quote as a starting price for price-based orders.
    // The field remains editable, so this is only a convenience default.
    useEffect(() => {
        if (!['LIMIT', 'MIDPRICE', 'STOP'].includes(orderType)) return;
        if (price || marketPrice === null || !marketPricePresentation.usableForPricing) return;
        setPrice(marketPrice.toFixed(2));
    }, [marketPrice, marketPricePresentation.usableForPricing, orderType, price]);

    // Auto-clear error messages after 8 seconds
    useEffect(() => {
        if (error) {
            const timer = setTimeout(() => setError(null), 8000);
            return () => clearTimeout(timer);
        }
    }, [error]);

    // Auto-clear success messages after 5 seconds
    useEffect(() => {
        if (success) {
            const timer = setTimeout(() => setSuccess(null), 5000);
            return () => clearTimeout(timer);
        }
    }, [success]);

    const applyContractSelection = useCallback((contract: ContractSelection) => {
        setSymbol((contract.local_symbol || contract.symbol || '').toUpperCase());
        if (contract.sec_type) setSecType(contract.sec_type.toUpperCase());
        setPrice('');
        setSelectedContract(contract);
        setLookupResults([]);
        setShowDropdown(false);
        clearQuote();
        if (contract.current_price !== undefined && contract.current_price !== null) {
            setQuoteSnapshot(Number(contract.current_price), {
                price: Number(contract.current_price),
                stale: Boolean(contract.current_price_stale),
                delayed: Boolean(contract.current_price_delayed) || isFutureContract(contract),
                received_at: contract.current_price_received_at,
            });
        }
        if (isQualifiedContract(contract)) {
            fetchContractQuote(contract, isFutureContract(contract));
        } else {
            fetchQuote(contract.symbol || '');
        }
    }, [clearQuote, fetchContractQuote, fetchQuote, setLookupResults, setQuoteSnapshot]);

    const handleSnap = () => {
        if (marketPrice !== null && marketPricePresentation.usableForPricing) {
            setPrice(marketPrice.toFixed(2));
        }
    };

    const handleFormSubmit = (e: React.FormEvent) => {
        e.preventDefault();
    };

    const handlePlaceOrderClick = useManualOrderSubmission({
        editingOrder,
        unsupportedOrderType,
        symbol,
        action,
        quantity,
        orderType,
        price,
        trailingAmount,
        trailType,
        tif,
        outsideRth,
        secType,
        strategyName,
        selectedContract,
        setLoading,
        setError,
        setSuccess,
        resetForm,
        onOrderModified,
        onOrderSubmitted,
    });

    return (
        <div id={inModal ? undefined : 'manual-order-entry'} className={`panel w-full ${inModal ? 'modal-form-shell' : 'full-bleed-panel lg:h-full'}`}>
            <div className="px-6 py-5 border-b border-white/10">
                <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${editingOrder ? 'bg-warning-500/10 text-warning-500' : 'bg-neon/10 text-neon'}`}>
                        {editingOrder ? <Pencil className="w-5 h-5" /> : <Zap className="w-5 h-5 fill-current" />}
                    </div>
                    <div className="space-y-0.5">
                        <h3 className="text-lg font-bold text-white tracking-tight">{editingOrder ? 'Modify Order' : prefillOrder ? 'Close Position' : 'Manual Order'}</h3>
                        <p className="text-xs text-muted">
                            {editingOrder ? `Editing open order #${editingOrder.order_id}` : prefillOrder ? 'Review the closing order before sending it to IB' : 'Discretionary trade execution via IB'}
                        </p>
                    </div>
                </div>
                {editingOrder && (
                    <div className="mt-4 rounded-lg border border-warning-500/25 bg-warning-500/10 px-3 py-2 text-[11px] text-warning-500">
                        Update the current IB order, then submit the modification. The instrument stays locked to this order.
                    </div>
                )}
                {unsupportedOrderType && (
                    <div className="mt-2 rounded-lg border border-error-500/25 bg-error-500/10 px-3 py-2 text-[11px] text-error-500">
                        This order is <span className="font-mono font-semibold">{unsupportedOrderType}</span>, which this editor does not support. Cancel editing and use IBKR/TWS so the order type and protective price are preserved.
                    </div>
                )}
            </div>

            <div className="p-4">
                <form onSubmit={handleFormSubmit} className="space-y-4">
                    {/* Row 1: Symbol and qualified-contract selection */}
                    <ContractPicker
                        symbol={symbol}
                        secType={secType}
                        isEditing={Boolean(editingOrder)}
                        positionSuggestions={positionSuggestions}
                        lookupResults={lookupResults}
                        lookupLoading={lookupLoading}
                        showDropdown={showDropdown}
                        selectedContract={selectedContract}
                        marketPrice={marketPrice}
                        fetchingPrice={fetchingPrice}
                        pricePresentation={marketPricePresentation}
                        onSymbolChange={(nextSymbol) => {
                            setSymbol(nextSymbol);
                            setSelectedContract(null);
                            setLookupResults([]);
                            clearQuote();
                            setPrice('');
                            if (!showDropdown) setShowDropdown(true);
                        }}
                        onSymbolFocus={() => {
                            setError(null);
                            setShowDropdown(true);
                        }}
                        onSymbolBlur={() => {
                            // Delay to allow clicking dropdown items.
                            setTimeout(() => setShowDropdown(false), 200);
                        }}
                        onSecTypeChange={(value) => {
                            setSecType(value);
                            setSelectedContract(null);
                            setLookupResults([]);
                            clearQuote();
                            setPrice('');
                        }}
                        onLookup={handleLookup}
                        onSelectContract={applyContractSelection}
                        onRefreshQuote={refreshSelectedQuote}
                    />

                    {/* Row 2: Side and quantity */}
                    <OrderSideQuantityFields
                        action={action}
                        quantity={quantity}
                        onActionChange={setAction}
                        onQuantityChange={setQuantity}
                    />

                    {/* Row 3: Order type and price inputs */}
                    <OrderPricingFields
                        orderType={orderType}
                        price={price}
                        trailingAmount={trailingAmount}
                        trailType={trailType}
                        action={action}
                        marketPrice={marketPrice}
                        fetchingPrice={fetchingPrice}
                        pricePresentation={marketPricePresentation}
                        currency={selectedContract?.currency || 'USD'}
                        onOrderTypeChange={(value) => {
                            setOrderType(value);
                            if (['LIMIT', 'MIDPRICE', 'STOP'].includes(value) && !price && marketPrice !== null && marketPricePresentation.usableForPricing) {
                                setPrice(marketPrice.toFixed(2));
                            }
                            if (value === 'MARKET' || value === 'MIDPRICE') {
                                setTif('DAY');
                                setOutsideRth(false);
                            }
                        }}
                        onPriceChange={setPrice}
                        onTrailingAmountChange={setTrailingAmount}
                        onTrailTypeChange={setTrailType}
                        onRefreshQuote={refreshSelectedQuote}
                        onSnapToMarket={handleSnap}
                    />

                    <OrderExecutionOptionsFields
                        timeInForce={tif}
                        outsideRegularHours={outsideRth}
                        orderType={orderType}
                        onTimeInForceChange={setTif}
                        onOutsideRegularHoursChange={setOutsideRth}
                    />

                    {/* Status Messages */}
                    {error && (
                        <div className="flex items-center gap-2 text-xs text-error-500 bg-error-500/10 p-2 rounded border border-error-500/20">
                            <AlertCircle className="w-3 h-3 flex-shrink-0" />
                            <span>{error}</span>
                        </div>
                    )}
                    {success && (
                        <div className="flex items-center gap-2 text-xs text-success-500 bg-success-500/10 p-2 rounded border border-success-500/20">
                            <CheckCircle className="w-3 h-3 flex-shrink-0" />
                            <span>{success}</span>
                        </div>
                    )}

                    {/* Submit Button */}
                    <div className="flex min-w-0 gap-2">
                        {(editingOrder || showCancelButton) && (
                            <ThemeButton variant="ghost" size="xs"
                                type="button"
                                onClick={() => {
                                    resetForm();
                                    (onCancel || onCancelEditing)?.();
                                }}
                                disabled={loading}
                                aria-label="Cancel editing"
                                className="min-w-0 flex-1 flex items-center justify-center gap-1.5 uppercase tracking-[0.12em] leading-none"
                            >
                                <X className="w-3.5 h-3.5 shrink-0" />
                                <span className="truncate">CANCEL</span>
                            </ThemeButton>
                        )}
                        <ThemeButton variant={action === "BUY" ? "success" : "danger"} size="md"
                            type="button"
                            onClick={handlePlaceOrderClick}
                            disabled={loading || Boolean(unsupportedOrderType) || !symbol || !quantity || parseInt(quantity) <= 0}
                            className={editingOrder || showCancelButton ? "min-w-0 flex-[1.2]" : "w-full"}
                        >
                            {loading ? (
                                editingOrder ? 'Updating...' : 'Executing...'
                            ) : (
                                <>
                                    {editingOrder ? <Pencil className="w-3.5 h-3.5 shrink-0" /> : <Send className="w-3.5 h-3.5 shrink-0" />}
                                    <span className="truncate">{editingOrder ? `MODIFY ${action}` : `PLACE ${action}`}</span>
                                </>
                            )}
                        </ThemeButton>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ManualOrderEntry;
